import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const TAX_RATE = 0.0825;
const SIZES = ['S', 'M', 'L', 'XL'];

// Max quantity for a cart item based on its stock structure
function getMaxQty(item) {
  if (item.hasSize && item.selectedSize) {
    return typeof item.stock === 'object' && item.stock !== null
      ? (item.stock[item.selectedSize] ?? 99)
      : 99;
  }
  return typeof item.stock === 'number' ? item.stock : 99;
}

// Shared +/− button style
const qtyBtnStyle = {
  width: '32px',
  height: '32px',
  border: '1px solid #ddd',
  background: 'white',
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  userSelect: 'none',
  flexShrink: 0
};

function Cart() {
  const { cartItems, removeFromCart, updateQuantity, editCartItemSize, cartSubtotal } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Tracks which item's size picker is open (by cartKey), null = none
  const [editingCartKey, setEditingCartKey] = useState(null);

  const taxAmount = cartSubtotal * TAX_RATE;
  const total = cartSubtotal + taxAmount;

  function handleCheckout() {
    if (!currentUser) {
      navigate('/login');
    } else {
      navigate('/checkout');
    }
  }

  function handleSizeEdit(cartKey, newSize) {
    editCartItemSize(cartKey, newSize);
    setEditingCartKey(null); // close the picker
  }

  if (cartItems.length === 0) {
    return (
      <div className="container py-5 text-center">
        <div className="py-5">
          <h2 className="fw-bold text-uppercase mb-3" style={{ letterSpacing: '0.06em' }}>
            Your Cart is Empty
          </h2>
          <p className="text-muted">Nothing here yet. Go find something good.</p>
          <Link to="/products" className="btn btn-piggy mt-3 px-4">
            Shop Now
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <h4 className="fw-bold text-uppercase mb-4" style={{ letterSpacing: '0.08em' }}>
        Your Cart
      </h4>

      <div className="row g-4">

        {/* ----- Left: Cart Items ----- */}
        <div className="col-lg-8">
          {cartItems.map(item => (
            <div
              className="card mb-3 border-0 shadow-sm"
              style={{ borderRadius: '2px' }}
              key={item.cartKey}
            >
              <div className="card-body p-3">
                <div className="d-flex gap-3">

                  {/* Clickable image → product detail */}
                  <Link to={`/product/${item.id}`} style={{ flexShrink: 0 }}>
                    <img
                      src={item.images?.[0] || item.imageUrl || 'https://placehold.co/80x80/f4f4f4/888888?text=PC'}
                      alt={item.name}
                      style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '2px', display: 'block' }}
                    />
                  </Link>

                  {/* Product info */}
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex justify-content-between align-items-start gap-2">

                      {/* Name + size */}
                      <div>
                        {/* Clickable product name → detail page */}
                        <Link
                          to={`/product/${item.id}`}
                          className="fw-bold text-decoration-none"
                          style={{ color: '#111', fontSize: '0.95rem' }}
                        >
                          {item.name}
                        </Link>

                        {/* Size display + edit trigger — only for sized products */}
                        {item.hasSize && (
                          <div className="d-flex align-items-center gap-2 mt-1">
                            {item.selectedSize ? (
                              <span
                                className="small text-uppercase fw-semibold"
                                style={{ color: '#c84b11', letterSpacing: '0.04em' }}
                              >
                                Size: {item.selectedSize}
                              </span>
                            ) : (
                              <span className="small text-muted">No size selected</span>
                            )}

                            <button
                              className="btn btn-link p-0 small text-decoration-none"
                              style={{ color: '#888', fontSize: '0.75rem' }}
                              onClick={() =>
                                setEditingCartKey(
                                  editingCartKey === item.cartKey ? null : item.cartKey
                                )
                              }
                            >
                              {editingCartKey === item.cartKey ? 'Cancel' : 'Edit size'}
                            </button>
                          </div>
                        )}

                        {/* Inline size picker */}
                        {item.hasSize && editingCartKey === item.cartKey && (
                          <div className="d-flex gap-1 flex-wrap mt-2">
                            {SIZES.map(size => (
                              <button
                                key={size}
                                onClick={() => handleSizeEdit(item.cartKey, size)}
                                style={{
                                  width: '38px',
                                  height: '38px',
                                  border: size === item.selectedSize ? '2px solid #111' : '1px solid #ddd',
                                  background: size === item.selectedSize ? '#111' : 'white',
                                  color: size === item.selectedSize ? 'white' : '#111',
                                  fontWeight: 600,
                                  fontSize: '0.8rem',
                                  cursor: 'pointer',
                                  borderRadius: '2px',
                                  transition: 'all 0.1s ease'
                                }}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        )}

                        <p className="text-muted small mb-0 mt-1">${item.price.toFixed(2)} ea.</p>
                      </div>

                      {/* Line total */}
                      <span className="fw-bold" style={{ fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>

                    {/* Quantity controls + remove */}
                    <div className="d-flex align-items-center justify-content-between mt-3">
                      {/* +/− quantity buttons */}
                      <div
                        className="d-flex align-items-center"
                        style={{ border: '1px solid #ddd', borderRadius: '2px', width: 'fit-content' }}
                      >
                        <button
                          onClick={() => updateQuantity(item.cartKey, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          style={{ ...qtyBtnStyle, borderRight: '1px solid #ddd', borderRadius: '2px 0 0 2px' }}
                        >
                          −
                        </button>
                        <span style={{ width: '40px', textAlign: 'center', fontWeight: 600, fontSize: '0.9rem' }}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.cartKey, item.quantity + 1)}
                          disabled={item.quantity >= getMaxQty(item)}
                          style={{ ...qtyBtnStyle, borderLeft: '1px solid #ddd', borderRadius: '0 2px 2px 0' }}
                        >
                          +
                        </button>
                      </div>

                      {/* Remove link */}
                      <button
                        className="btn btn-link p-0 small text-decoration-none"
                        style={{ color: '#aaa', fontSize: '0.8rem' }}
                        onClick={() => removeFromCart(item.cartKey)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ----- Right: Order Summary ----- */}
        <div className="col-lg-4">
          <div
            className="card border-0 shadow-sm sticky-top"
            style={{ top: '80px', borderRadius: '2px' }}
          >
            <div className="card-body p-4">
              <h6 className="fw-bold text-uppercase mb-3" style={{ letterSpacing: '0.08em' }}>
                Summary
              </h6>
              <hr />
              <div className="d-flex justify-content-between mb-2 small">
                <span>Subtotal ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})</span>
                <span>${cartSubtotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between mb-2 small text-muted">
                <span>Tax (8.25%)</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between fw-bold mb-1">
                <span>Estimated Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <p className="small text-muted mb-3">Final total calculated at checkout.</p>

              <button
                className="btn btn-piggy w-100 mb-2"
                onClick={handleCheckout}
              >
                Checkout
              </button>
              <Link
                to="/products"
                className="btn btn-outline-secondary w-100"
                style={{ borderRadius: '2px' }}
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Cart;
